// @ts-nocheck — mixin methods are composed onto BackendService at runtime; instance API is typed via adminBackendServiceInterface.ts.
import type BaseBackendService from '../BaseBackendService';
export const adminBackendServiceNotificationMixin = {
  async getGoogleChatConnectUrl(): Promise<{ data: { url: string } }> {
    return this.authApiGetAuthed(
      '/api/admin/chatNotificationPreferences/google/connect-url',
      'Network error while fetching Google Chat connect URL',
      (json) => json as { data: { url: string } },
      true,
    );
  },

  async disconnectGoogleChat(): Promise<{
    data: {
      googleChatConnected: boolean;
      googleChatConnectionStatus: string | null;
      googleUserEmail: string | null;
      teamsChatConnected: boolean;
      teamsConnectionStatus: string | null;
      teamsUserEmail: string | null;
    };
  }> {
    return this.authApiMutateAuthed(
      '/api/admin/chatNotificationPreferences/google/disconnect',
      'POST',
      undefined,
      (json) => json,
      true,
    );
  },

  async getTeamsChatConnectUrl(): Promise<{ data: { url: string } }> {
    return this.authApiGetAuthed(
      '/api/admin/chatNotificationPreferences/teams/connect-url',
      'Network error while fetching Teams Chat connect URL',
      (json) => json as { data: { url: string } },
      true,
    );
  },

  async disconnectTeamsChat(): Promise<{
    data: {
      teamsChatConnected: boolean;
      teamsConnectionStatus: string | null;
      teamsUserEmail: string | null;
    };
  }> {
    return this.authApiMutateAuthed(
      '/api/admin/chatNotificationPreferences/teams/disconnect',
      'POST',
      undefined,
      (json) => json,
      true,
    );
  },

  async getSlackChatConnectUrl(): Promise<{ data: { url: string } }> {
    return this.authApiGetAuthed(
      '/api/admin/chatNotificationPreferences/slack/connect-url',
      'Network error while fetching Slack connect URL',
      (json) => json as { data: { url: string } },
      true,
    );
  },

  async disconnectSlackChat(): Promise<{
    data: {
      slackChatConnected: boolean;
      slackConnectionStatus: string | null;
      slackUserEmail: string | null;
    };
  }> {
    return this.authApiMutateAuthed(
      '/api/admin/chatNotificationPreferences/slack/disconnect',
      'POST',
      undefined,
      (json) => json,
      true,
    );
  },

  async getChatNotificationPreferences(): Promise<{
    data: {
      emailEnabled: boolean;
      emailTiming: 'immediate' | 'unanswered';
      emailUnansweredMinutes: number;
      googleChatEnabled: boolean;
      googleChatTiming: 'immediate' | 'unanswered';
      googleChatUnansweredMinutes: number;
      googleChatConnected: boolean;
      googleChatConnectedAt: string | null;
      googleChatConnectionStatus: string | null;
      googleUserEmail: string | null;
      teamsChatEnabled: boolean;
      teamsChatTiming: 'immediate' | 'unanswered';
      teamsChatUnansweredMinutes: number;
      teamsChatConnected: boolean;
      teamsConnectedAt: string | null;
      teamsConnectionStatus: string | null;
      teamsUserEmail: string | null;
      slackChatEnabled: boolean;
      slackChatTiming: 'immediate' | 'unanswered';
      slackChatUnansweredMinutes: number;
      slackChatConnected: boolean;
      slackConnectedAt: string | null;
      slackConnectionStatus: string | null;
      slackUserEmail: string | null;
    };
  }> {
    return this.authApiGetAuthed(
      '/api/admin/chatNotificationPreferences/me',
      'Network error while fetching chat notification preferences',
      (json) => json,
      true,
    );
  },

  async saveChatNotificationPreferences(preferences: {
    emailEnabled: boolean;
    emailTiming: 'immediate' | 'unanswered';
    emailUnansweredMinutes: number;
    googleChatEnabled: boolean;
    googleChatTiming: 'immediate' | 'unanswered';
    googleChatUnansweredMinutes: number;
    teamsChatEnabled: boolean;
    teamsChatTiming: 'immediate' | 'unanswered';
    teamsChatUnansweredMinutes: number;
    slackChatEnabled: boolean;
    slackChatTiming: 'immediate' | 'unanswered';
    slackChatUnansweredMinutes: number;
  }): Promise<{
    data: {
      emailEnabled: boolean;
      emailTiming: 'immediate' | 'unanswered';
      emailUnansweredMinutes: number;
      googleChatEnabled: boolean;
      googleChatTiming: 'immediate' | 'unanswered';
      googleChatUnansweredMinutes: number;
      googleChatConnected: boolean;
      googleChatConnectedAt: string | null;
      googleChatConnectionStatus: string | null;
      googleUserEmail: string | null;
      teamsChatEnabled: boolean;
      teamsChatTiming: 'immediate' | 'unanswered';
      teamsChatUnansweredMinutes: number;
      teamsChatConnected: boolean;
      teamsConnectedAt: string | null;
      teamsConnectionStatus: string | null;
      teamsUserEmail: string | null;
      slackChatEnabled: boolean;
      slackChatTiming: 'immediate' | 'unanswered';
      slackChatUnansweredMinutes: number;
      slackChatConnected: boolean;
      slackConnectedAt: string | null;
      slackConnectionStatus: string | null;
      slackUserEmail: string | null;
    };
  }> {
    return this.authApiMutateAuthed(
      '/api/admin/chatNotificationPreferences/me',
      'PUT',
      preferences,
      (json) => json,
      true,
    );
  },

  async getNotificationSubscriptionStatus(): Promise<{
    data: Array<{
      id: string;
      label: string;
      description: string;
      subscribed: boolean;
      subscribedByImplication: boolean;
      seen: boolean;
    }>;
    configured: boolean;
    configuredAt: string | null;
  }> {
    return this.authApiGetAuthed(
      '/api/admin/notificationSubscriptions/me/status',
      'Network error while fetching notification subscription status',
      (json) => json,
      true,
    );
  },

  async saveNotificationSubscriptions(
    subscriptions: Record<
      string,
      boolean | { subscribed: boolean; subscribedByImplication: boolean; seen?: boolean }
    >,
  ): Promise<{
    data: {
      message: string;
      success: boolean;
      results: Array<{
        eventType: string;
        action: string;
        success: boolean;
        error?: string;
      }>;
    };
  }> {
    return this.authApiMutateAuthed(
      '/api/admin/notificationSubscriptions/save',
      'POST',
      { subscriptions },
      (json) => json,
      true,
    );
  },

  checkAdminHasPushTokens(): Promise<{ hasPushTokens: boolean }> {
    return this.authApiGetAuthed(
      'admin/has-push-tokens',
      'Network error while checking admin push tokens',
      (json) => json as { hasPushTokens: boolean },
    );
  },

  getContactPushTokens(_contactId: string): Promise<
    Array<{
      id: string;
      token: string;
      deviceType: 'ios' | 'android' | 'web';
      lastUsedAt?: string;
      isActive: boolean;
    }>
  > {
    return Promise.resolve([]);
  },

  sendTestNotification(
    contactId: string,
    _payload: { title: string; body: string },
  ): Promise<void> {
    return this.sendContactDeepLink(contactId).then(() => undefined);
  },

  sendContactDeepLink(contactId: string): Promise<{ sent: number; ticketIds?: string[] }> {
    return this.authApiMutateAuthed(
      `admin/contacts/${contactId}/send-deep-link`,
      'POST',
      undefined,
      (json) => json,
    );
  },
} satisfies ThisType<BaseBackendService>;
